import { useMemo } from 'react';
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowForward,
  Clear,
  Inventory2Outlined,
  Refresh,
  Search,
} from '@mui/icons-material';
import { type GridColDef, type GridRowParams } from '@mui/x-data-grid';

import { SAMPLE_PICKER_RESULT_LIMIT } from '@/api/sample/sample';
import { type SampleSearchResult } from '@/api/sample/types';
import AppDataGrid, {
  GridPrimaryText,
  GridSecondaryText,
} from '@/components/DataGrid';
import CustomNoRowsOverlay from '@/components/CustomNoRowsOverlay';

interface SampleSearchProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  samples: SampleSearchResult[];
  selectedSampleId?: string;
  onSelectSample: (sample: SampleSearchResult) => void;
  isLoading: boolean;
  isError: boolean;
  minimumSearchLength: number;
  searchIsValid: boolean;
  onRetry: () => void;
}

const sampleTypeLabels: Record<string, string> = {
  bam: 'BAM',
  paired_fastq: 'Paired FASTQ',
  unpaired_fastq: 'Unpaired FASTQ',
};

const NoRecentSamplesOverlay = () => (
  <CustomNoRowsOverlay message="No recently updated active samples" />
);

const NoMatchingSamplesOverlay = () => (
  <CustomNoRowsOverlay message="No active samples match this search" />
);

const SearchPromptOverlay = () => (
  <CustomNoRowsOverlay message="Continue typing to search samples" />
);

const SamplesUnavailableOverlay = () => (
  <CustomNoRowsOverlay message="Sample results are currently unavailable" />
);

const formatUpdatedDate = (date?: string) => {
  if (!date) return '—';

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '—';

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsedDate);
};

const SampleSearch = ({
  searchValue,
  onSearchChange,
  onClearSearch,
  samples,
  selectedSampleId,
  onSelectSample,
  isLoading,
  isError,
  minimumSearchLength,
  searchIsValid,
  onRetry,
}: SampleSearchProps) => {
  const theme = useTheme();
  const compactView = useMediaQuery(theme.breakpoints.down('sm'));
  const normalizedSearch = searchValue.trim();
  const isRecentView = normalizedSearch.length === 0;
  const visibleSelectedSampleId = samples.some(
    (sample) => sample.id === selectedSampleId,
  )
    ? selectedSampleId
    : undefined;
  let NoRowsOverlay = isRecentView
    ? NoRecentSamplesOverlay
    : NoMatchingSamplesOverlay;

  if (!searchIsValid) {
    NoRowsOverlay = SearchPromptOverlay;
  } else if (isError) {
    NoRowsOverlay = SamplesUnavailableOverlay;
  }

  const columns = useMemo<GridColDef<SampleSearchResult>[]>(
    () => [
      {
        field: 'sample_id',
        headerName: 'Sample ID',
        flex: 1.3,
        minWidth: 180,
        renderCell: (params) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <GridPrimaryText>{params.value}</GridPrimaryText>
            {compactView && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {sampleTypeLabels[params.row.sample_type] ||
                  params.row.sample_type}
                {' · '}
                {params.row.gender}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'sample_type',
        headerName: 'Sample type',
        flex: 0.9,
        minWidth: 150,
        renderCell: (params) => (
          <Chip
            label={sampleTypeLabels[params.value] || params.value}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: 'gender',
        headerName: 'Gender',
        flex: 0.65,
        minWidth: 110,
        renderCell: (params) => (
          <GridSecondaryText
            value={
              params.value
                ? `${params.value.charAt(0).toUpperCase()}${params.value.slice(
                    1,
                  )}`
                : '—'
            }
          />
        ),
      },
      {
        field: 'updated_at',
        headerName: 'Last updated',
        flex: 1,
        minWidth: 190,
        renderCell: (params) => (
          <GridSecondaryText value={formatUpdatedDate(params.value)} />
        ),
      },
      {
        field: 'review',
        headerName: 'Analysis',
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        width: 120,
        renderCell: (params) => (
          <Button
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            onClick={(event) => {
              event.stopPropagation();
              onSelectSample(params.row);
            }}
          >
            Review
          </Button>
        ),
      },
    ],
    [compactView, onSelectSample],
  );

  const handleRowClick = (params: GridRowParams<SampleSearchResult>) => {
    onSelectSample(params.row);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Stack spacing={0.35} sx={{ minWidth: 220 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3">Sample registry</Typography>
            {!isLoading && !isError && searchIsValid && (
              <Chip
                label={`${samples.length} shown`}
                size="small"
                sx={{ bgcolor: 'primary.light', color: 'primary.main' }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {isRecentView
              ? 'Recently updated active samples.'
              : 'Search active samples by Sample ID.'}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ width: { xs: '100%', md: 'min(520px, 48%)' } }}
        >
          <TextField
            id="sample-registry-search"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            label="Search Sample ID"
            placeholder="Type at least 2 characters"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment position="end">
                  <Tooltip title="Clear search">
                    <IconButton
                      size="small"
                      aria-label="clear sample search"
                      onClick={onClearSearch}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Tooltip title="Refresh samples">
            <span>
              <IconButton
                aria-label="refresh samples"
                onClick={onRetry}
                disabled={isLoading || !searchIsValid}
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {!searchIsValid && (
        <Alert severity="info" icon={<Search />} sx={{ borderRadius: 0 }}>
          Enter at least {minimumSearchLength} characters to search samples.
        </Alert>
      )}

      {isError && searchIsValid && (
        <Alert
          severity="error"
          sx={{ borderRadius: 0 }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Samples could not be loaded. Please try again.
        </Alert>
      )}

      <AppDataGrid
        rows={searchIsValid ? samples : []}
        columns={columns}
        loading={isLoading && searchIsValid}
        getRowId={(row) => row.id || row.sample_id}
        onRowClick={handleRowClick}
        rowSelectionModel={
          visibleSelectedSampleId ? [visibleSelectedSampleId] : []
        }
        columnVisibilityModel={{
          sample_type: !compactView,
          gender: !compactView,
          updated_at: !compactView,
        }}
        hideFooter
        slots={{ noRowsOverlay: NoRowsOverlay }}
        sx={(currentTheme) => ({
          border: 0,
          borderRadius: 0,
          minHeight: 210,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-row.Mui-selected': {
            bgcolor: alpha(
              currentTheme.palette.primary.main,
              currentTheme.palette.mode === 'dark' ? 0.24 : 0.12,
            ),
            boxShadow: `inset 3px 0 0 ${currentTheme.palette.primary.main}`,
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            bgcolor: alpha(
              currentTheme.palette.primary.main,
              currentTheme.palette.mode === 'dark' ? 0.3 : 0.17,
            ),
          },
        })}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={0.75}
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.25,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Inventory2Outlined fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary">
            Select a sample to review linked experiments and results.
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Displays up to {SAMPLE_PICKER_RESULT_LIMIT} server-filtered matches;
          this is a result cap, not pagination.
        </Typography>
      </Stack>
    </Paper>
  );
};

export default SampleSearch;
